import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faSearchDollar } from "@fortawesome/pro-solid-svg-icons/faSearchDollar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import LeadFinderPopover from "@app/scene/map/components/LeadFinderPopover";
import ProcessingModal from "@app/scene/map/components/LeadFinderPopover/components/ProcessingModal";
import { hideLeadFinder, showLeadFinder } from "@app/scene/map/store/leadFinder/actions";
import { isConfigurationVisible } from "@app/scene/map/store/leadFinder/selectors";
import { getLeadGenPlan, isCurrentUserOwner, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import styles from "./LeadFinderButton.module.scss";

interface Props {
  hideLeadFinder: typeof hideLeadFinder;
  isOwner: boolean;
  isReadOnlyMember: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  showLeadFinder: typeof showLeadFinder;
  visible: boolean;
}

const messages = defineMessages({
  title: {
    id: "map.toolbar.topCommands.leadFinder",
    defaultMessage: "Lead Finder",
    description: "Lead Finder button title",
  },
});

const LeadFinderButton: React.FC<Props> = ({
  hideLeadFinder,
  isOwner,
  isReadOnlyMember,
  leadGenPlan,
  showLeadFinder,
  visible,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const [defaultLeadSearchQuery, setDefaultLeadSearchQuery] = useState<string>("");

  const { search } = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    if (urlParams.has("findLeads")) {
      if (urlParams.has("findLeadsQuery")) {
        setDefaultLeadSearchQuery(urlParams.get("findLeadsQuery") ?? "");
        urlParams.delete("findLeadsQuery");
      }
      showLeadFinder();

      // delete from url
      urlParams.delete("findLeads");
      window.history.replaceState(
        {},
        document.title,
        `${window.location.origin}${window.location.pathname}?${urlParams.toString()}${
          window.location.hash
        }`
      );
    }
  }, [showLeadFinder, search, setDefaultLeadSearchQuery]);

  const handleVisibleChange = useCallback(
    (visible) => {
      if (visible) {
        showLeadFinder();
        analytics.initiated(["Find Leads button"]);
      } else {
        hideLeadFinder();
      }

      if (leadGenPlan === LeadFinderSubscriptionPlan.NONE) {
        showLeadFinderUpsell(leadGenPlan, intl, isOwner);
      }
    },
    [analytics, hideLeadFinder, intl, isOwner, leadGenPlan, showLeadFinder]
  );

  if (isReadOnlyMember) {
    return null;
  }

  return (
    <Interactive>
      <Popover
        content={<LeadFinderPopover defaultQuery={defaultLeadSearchQuery} />}
        destroyTooltipOnHide
        onOpenChange={handleVisibleChange}
        open={visible}
        placement="bottomRight"
        trigger="click"
      >
        <Button
          className={cn(styles.btn, { [styles.active]: visible })}
          rightIcon={<FontAwesomeIcon icon={faSearchDollar} />}
          type="text"
        >
          {intl.formatMessage(messages.title)}
        </Button>
      </Popover>

      <ProcessingModal />
    </Interactive>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  leadGenPlan: getLeadGenPlan(state),
  visible: isConfigurationVisible(state),
});

const mapDispatchToProps = {
  hideLeadFinder,
  showLeadFinder,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadFinderButton);
